import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Moon, Sun, Menu, X } from "lucide-react";
import { useTheme } from "../lib/theme";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "framer-motion";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/portfolio", label: "Work" },
    { to: "/about", label: "About" },
    { to: "/careers", label: "Careers" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const { theme, toggle } = useTheme();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "glass border-b border-border/60"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="navbar-logo"
                    className="flex items-center gap-2 group"
                >
                    <div className="w-8 h-8 rounded-lg bg-foreground text-background grid place-items-center font-display font-bold text-sm">
                        T
                    </div>
                    <span className="font-display text-lg tracking-tight">
                        Thooyra
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-3 py-2 text-sm rounded-md transition-colors ${
                                    isActive
                                        ? "text-foreground"
                                        : "text-muted-foreground hover:text-foreground"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        data-testid="theme-toggle"
                        aria-label="Toggle theme"
                        onClick={toggle}
                        className="w-9 h-9 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                        {theme === "dark" ? (
                            <Sun className="h-4 w-4" />
                        ) : (
                            <Moon className="h-4 w-4" />
                        )}
                    </button>
                    <Link to="/contact" className="hidden sm:block">
                        <Button
                            data-testid="navbar-cta"
                            className="rounded-full px-5"
                        >
                            Book a call
                        </Button>
                    </Link>
                    <button
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                        onClick={() => setOpen((o) => !o)}
                        className="md:hidden w-9 h-9 grid place-items-center rounded-md hover:bg-secondary"
                    >
                        {open ? (
                            <X className="h-5 w-5" />
                        ) : (
                            <Menu className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="md:hidden glass border-t border-border/60"
                    >
                        <div className="px-6 py-4 flex flex-col gap-1">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `py-3 text-sm border-b border-border/40 ${
                                            isActive
                                                ? "text-foreground"
                                                : "text-muted-foreground"
                                        }`
                                    }
                                    end={l.to === "/"}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
